import React from 'react';
import ContentToggle from '../../../../components/common/ContentToggle';
import PopUp from '../../../../components/common/PopUp/PopUp';
import ExternalIcon from '../../../../../public/assets/images/signifier.svg';

import {
  valueCompassHelperTextContent,
  focusAreaHelperTextContent,
  smartGoalHelperTextContent,
  goalSettingHelperTextContent,
  linkToMetricsInformation,
  toolUsingHelperText,
  ideaHelperText,
  toolAndTipsHelperText,
  testOfChangeHelperText,
  resultSummaryHelperText,
  resultDescHelperText,
  learningPracticeHelperText,
  numeratoreHelperText,
  calculatedValueLabelHelperText,
  denominatorHelperText,
  additionalVarialbeLabelHelperText,
  performanceMetricHelperTextContent,
  annualROIHelperText,
} from '../ProjectHelper/ProjectDetailsHelperText';
import { formatTimestampMDY } from '../../../../utils/date';

const DisplayProjectContainer: React.FC<{ project?: any; selectedProject?: any; }> = ({ project, selectedProject }) => {
  const shouldShowCompletedDate = ['Complete', 'Closed'].includes(project?.perfStatus);

  const formatCurrency = (val: any) => {
    if (val === null || val === undefined || val === '') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(0);
    }
    const num = Number(String(val).replaceAll(/[^0-9.-]+/g, ''));
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };
  const formatOneDecimal = (val: any) => {
    if (val === null || val === undefined || val === '') return '0.0';
    const num = Number(String(val).replaceAll(/[^0-9.-]+/g, ''));
    if (Number.isNaN(num)) return '0.0';
    return Number(num).toFixed(1);
  };

  return (
    <>
      <ContentToggle titleText={'What are we trying to accomplish?'} accordionOption>
        <div className="p-4 pr-[264px]! items-center  w-full justify-between flex">
          <div className="flex-1">
            <div className="flex flex-col gap-4!">
              <div className="flex items-start w-full">
                <div className="w-57!">
                  <p className="font-semibold mb-2!">Name of project</p>
                </div>
                <div className="flex-1">
                  <p className="p4 mb-2!">{selectedProject?.nameOfProject ?? ''}</p>
                </div>
              </div>

              <div className="flex items-start w-full">
                <div className="w-57!">
                  <p className="font-semibold mb-2!">
                    <span className="inline-flex items-center gap-1">Value compass <PopUp message={valueCompassHelperTextContent} /></span>
                  </p>
                </div>
                <div className="flex-1">
                  <p className="p4 mb-2!">{project?.valueCompassDesc ?? ''}</p>
                </div>
              </div>

              <div className="flex items-start w-full">
                <div className="w-57!">
                  <p className="font-semibold mb-4!">
                    <span className="inline-flex items-center gap-1">Focus area <PopUp message={focusAreaHelperTextContent} /></span>
                  </p>
                </div>
                <div className="flex-1">
                  <p className="p4 mb-2!">{selectedProject?.focusArea ?? ''}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex">
            <div className="flex flex-col items-end text-right mr-4!">
              <p className="font-semibold mb-4!">Status:</p>
              <p className="font-semibold mb-4!">Start date:</p>
              {shouldShowCompletedDate && (
                <p className="font-semibold mb-4!">Completed date:</p>
              )}
            </div>
            <div>
              <p className="p4 mb-4!">{selectedProject?.status ?? ''}</p>
              <p className="p4 mb-4!">{selectedProject?.startDate ? formatTimestampMDY(selectedProject.startDate) : ''}</p>
              {shouldShowCompletedDate && (
                <p className="p4 mb-4!">{project?.perfEndDate ? formatTimestampMDY(project.perfEndDate) : ''}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex pr-[200px]!">
          <div className="font-semibold min-w-57!">
            <span className="inline-flex items-center gap-1">Our SMART goal <PopUp message={smartGoalHelperTextContent} /></span>
          </div>
          <p className="p4 whitespace-pre-wrap">{project?.perfGoal ?? ''}</p>
        </div>
      </ContentToggle>
      <hr className="hr-light" />
      <ContentToggle titleText="How will we know that change is an improvement?" accordionOption={false}>
        <div className="p-4 pr-[264px]! w-full">
          <div className="flex flex-col w-full gap-4">
            {/* Performance metric row */}
            <div className="flex items-start w-full">
              <div className="min-w-[220px]!">
                <p className="font-semibold mb-4!">
                  <span className="inline-flex items-center gap-1">Performance metric <PopUp message={performanceMetricHelperTextContent} /></span>
                </p>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-5! w-full">
                  <p className="p4 flex-1">{selectedProject?.performanceMetric ?? ''}</p>
                  <div className="flex items-center gap-2 justify-end">
                    <p className="font-semibold">PSY Goal?</p>
                    <p className="p4">{project?.perfPspGoal ?? ''}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data source row */}
            <div className="flex items-start w-full">
              <div className="min-w-[220px]!">
                <p className="font-semibold mb-4!">Data source for metric</p>
              </div>
              <div className="flex-1">
                <p className="p4 mb-5!">{project?.dataDesc ?? ''}</p>
              </div>
            </div>

            {/* Goal setting row */}
            <div className="flex items-start w-full">
              <div className="min-w-[220px]!">
                <p className="font-semibold mb-4!">
                  <span className="inline-flex items-center gap-1">Goal setting <PopUp message={goalSettingHelperTextContent} /></span>
                </p>
              </div>
              <div className="flex-1">
                <div className="flex mb-5! gap-[95px]">
                  <div className="flex">
                    <p className="font-semibold">Baseline:&nbsp;</p>
                    <p className="p4">{formatOneDecimal(project?.improvementBaseline)}</p>
                  </div>
                  <div className="flex">
                    <p className="font-semibold">Threshold:&nbsp;</p>
                    <p className="p4">{formatOneDecimal(project?.improvementThreshold)}</p>
                  </div>
                  <div className="flex">
                    <p className="font-semibold">Target:&nbsp;</p>
                    <p className="p4">{formatOneDecimal(project?.improvementTarget)}</p>
                  </div>
                  <div className="flex">
                    <p className="font-semibold">Stretch:&nbsp;</p>
                    <p className="p4">{formatOneDecimal(project?.improvementStretch)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Links to metric information row */}
            <div className="flex items-start w-full">
              <div className="min-w-[220px]!">
                <p className="font-semibold mb-4!">
                  <span className="inline-flex items-center gap-1">Links to metric information <PopUp message={linkToMetricsInformation} /></span>
                </p>
              </div>
              <div className="flex-1">
                {project?.metricUrl ? (
                  <div className="flex items-center mb-4! gap-1">
                    <a href={project?.metricUrl} className="font-semibold" target="_blank" rel="noopener noreferrer">
                      Metric Reports
                    </a>
                    <img className="h-4 w-4" src={ExternalIcon} alt="" />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />
      <ContentToggle titleText="What changes can we make that result in improvement?" accordionOption={false}>
        <div className="p-4 pr-[264px]! w-full">
          <div className="flex flex-col w-full gap-4">
            {/* What tools are we using? */}
            <div className="flex items-start w-full">
              <div className="min-w-[230px]!">
                <p className="font-semibold mb-4!">
                  <span className="inline-flex items-center gap-1">What tools are we using? <PopUp message={toolUsingHelperText} /></span>
                </p>
              </div>
              <div className="flex-1">
                <p className="p4 mb-5!">{project?.listOfToolsUsed ?? ''}</p>
              </div>
            </div>

            {/* Ideas from UBTs (links) */}
            <div className="flex items-start w-full">
              <div className="min-w-[230px]!">
                <p className="font-semibold mb-4!">
                  <span className="inline-block">Ideas from UBTs doing</span>
                  <br />
                  <span className="inline-block">similar work?</span>
                  <span className="inline-flex ml-1">
                    <PopUp message={ideaHelperText} />
                  </span>
                </p>
              </div>
              <div className="flex-1">
                <div className="flex mb-9! gap-6">
                  {project?.websiteUrl ? (
                    <div className="flex items-center gap-1">
                      <a href={project?.websiteUrl} className="font-semibold" target="_blank" rel="noopener noreferrer">
                        LMP website
                      </a>
                      <img className="h-4 w-4" src={ExternalIcon} alt="" />
                    </div>
                  ) : null}
                  {project?.smartbookUrl ? (
                    <div className="flex items-center gap-1">
                      <a href={project?.smartbookUrl} className="font-semibold" target="_blank" rel="noopener noreferrer">
                        SmartBook
                      </a>
                      <img className="h-4 w-4" src={ExternalIcon} alt="" />
                    </div>
                  ) : null}
                  {project?.ideaBookUrl ? (
                    <div className="flex items-center gap-1">
                      <a href={project?.ideaBookUrl} className="font-semibold" target="_blank" rel="noopener noreferrer">
                        sharepoint
                      </a>
                      <img className="h-4 w-4" src={ExternalIcon} alt="" />
                    </div>
                  ) : null}
                  {project?.wikiUrl ? (
                    <div className="flex items-center gap-1">
                      <a href={project?.wikiUrl} className="font-semibold" target="_blank" rel="noopener noreferrer">
                        PI Wiki
                      </a>
                      <img className="h-4 w-4" src={ExternalIcon} alt="" />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Tools and tips (Data Chart) */}
            <div className="flex items-start w-full">
              <div className="min-w-[230px]!">
                <p className="font-semibold mb-4!">
                  <span className="inline-flex items-center gap-1">Tools and tips <PopUp message={toolAndTipsHelperText} /></span>
                </p>
              </div>
              <div className="flex-1">
                {project?.dataChartsUrl ? (
                  <div className="flex items-center mb-6! gap-1">
                    <a href={project?.dataChartsUrl} className="font-semibold" target="_blank" rel="noopener noreferrer">
                      Data Chart
                    </a>
                    <img className="h-4 w-4" src={ExternalIcon} alt="" />
                  </div>
                ) : null}
              </div>
            </div>

            {/* Test of change (button) */}
            <div className="flex items-start w-full">
              <div className="min-w-[230px]!">
                <div className="flex items-center font-semibold mb-4! gap-1">
                  <span className="inline-flex items-center gap-1">Test of change <PopUp message={testOfChangeHelperText} /></span>
                </div>
              </div>
              <div className="flex-1">
                <p className="p4">
                  <button
                    onClick={() => {
                      try {
                        const tabLinks = Array.from(document.querySelectorAll('.ds-tabs__link'));
                        const matchIndex = tabLinks.findIndex(t => (t.textContent ?? '').trim() === 'Test of Change');
                        const idx = matchIndex >= 0 ? matchIndex : 1;
                        if (tabLinks && tabLinks.length > idx) (tabLinks[idx] as HTMLElement).click();
                      } catch (err) {
                        console.error('Failed to switch to Test of Change tab:', err);
                      }
                    }}
                    className="text-kp-blue-dark! underline bg-transparent border-none cursor-pointer p-0"
                  >
                    Click here
                  </button>{' '}
                  to record your test of change
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />
      <ContentToggle titleText="What results did we obtain?" accordionOption={false}>
        <div className="mb-4! flex items-center">
          <div className="flex items-center">
              <div className="flex items-center font-semibold min-w-[220px]! gap-1">
              <span className="inline-flex items-center gap-1">Results summary <PopUp message={resultSummaryHelperText} /></span>
            </div>
            <p className="p4 w-[550px]">{project?.resultDesc ?? ''}</p>
          </div>
          <div className="flex items-center">
              <div className="flex items-center font-semibold min-w-[100px]!">
              <span className="inline-flex items-center gap-1">Annual ROI <PopUp message={annualROIHelperText} /></span>
            </div>
            <p className="p4">{formatCurrency(project?.roi)}</p>
          </div>
        </div>
        <div className="mb-4! flex items-center">
          <div className="flex items-center font-semibold min-w-[220px]! gap-1">
            <span className="inline-flex items-center gap-1">Results Description <PopUp message={resultDescHelperText} /></span>
          </div>
          <p className="p4 whitespace-pre-wrap">{project?.pipResultDesc ?? ''}</p>
        </div>
        <div className="mb-4! flex items-center">
            <div className="flex font-semibold min-w-[220px]! gap-1">
            <p className="max-w-[163px]! font-semibold ">Learnings or successful practices</p>
            <span className="inline-flex gap-1"><PopUp message={learningPracticeHelperText} /></span>
          </div>
          <p className="p4 whitespace-pre-wrap">{project?.perfLearnings ?? ''}</p>
        </div>
      </ContentToggle>
      <hr className="hr-light" />
      <ContentToggle titleText="Define & track your outcome measure:" accordionOption={false}>
        <div className="mb-4! flex items-center">
          <div className="flex items-center">
              <div className="flex items-center font-semibold min-w-[380px]! gap-1">
              <span className="inline-flex items-center gap-1">Numerator label (Top) <PopUp message={numeratoreHelperText} /></span>
            </div>
            <p className="p4 w-[425px]!">{project?.numeratorName ?? ''}</p>
          </div>
          <div className="flex items-center">
            <div className="flex items-center font-semibold min-w-[100px]! gap-1">
              <span className="inline-flex items-center gap-1">Denominator label (bottom) <PopUp message={denominatorHelperText} /></span>
            </div>
            <p className="p4">{project?.denominatorName ?? ''}</p>
          </div>
        </div>
        <div className="mb-4! flex items-center">
          <div className="mb-4! flex">
              <div className="flex gap-1 min-w-[380px]!">
              <p className="w-[152px]! font-semibold ">Calculated value label (Top/bottom)</p>
              <span className="inline-flex gap-1"><PopUp message={calculatedValueLabelHelperText} /></span>
            </div>
            <p className="p4 w-[425px]!">{project?.valueName ?? ''}</p>
          </div>
          <div className="flex items-center">
              <div className="flex items-center font-semibold min-w-[100px]! gap-1">
              <span className="inline-flex items-center gap-1">Additional variable label <PopUp message={additionalVarialbeLabelHelperText} /></span>
            </div>
            <p className="p4">{project?.outsideVarName ?? ''}</p>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />
      <ContentToggle titleText="Project information documents" accordionOption={false}>
        <div className="p-4">
          <div className="flex items-center gap-8">
            <p className="font-semibold">Team project documentation</p>
            {project?.urlToTeam ? (
              <div className="flex items-center gap-1">
                <a href={project?.urlToTeam} className="font-semibold" target="_blank" rel="noopener noreferrer">
                  Team Documentation
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />
    </>
  );
};

export default DisplayProjectContainer;
